import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Container,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    TextField,
    MenuItem,
    IconButton,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Divider,
    Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AddCircle, Cancel, Close, Save } from '@mui/icons-material';

const API_BASE_URL = 'http://155.133.27.173:3020';

// Create a theme instance
const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

const DepartmentManagement = () => {
    // State management
    const [view, setView] = useState('list');
    const [departments, setDepartments] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [formValues, setFormValues] = useState({ name: '', company: '' });
    const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState(null);

    // Fetch departments and companies
    useEffect(() => {
        fetchDepartments();
        fetchCompanies();
    }, []);

    const fetchDepartments = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/get-departments/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setDepartments(data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            showAlert('Failed to fetch departments', 'error');
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/get-company/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setCompanies(data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            showAlert('Failed to fetch companies', 'error');
        }
    };

    // Form handling
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Create department
    const handleCreateDepartment = async () => {
        try {
            const { name, company } = formValues;
            if (!name || !company) {
                showAlert('Please fill all required fields', 'error');
                return;
            }

            const formData = new URLSearchParams();
            formData.append("userID", localStorage.getItem("userID"));
            formData.append("name", name);
            formData.append("company", company);

            const response = await fetch(`${API_BASE_URL}/post-department`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData,
            });

            const result = await response.json();
            if (result._id) {
                showAlert('Department created successfully', 'success');
                setView('list');
                fetchDepartments();
                resetForm();
            } else {
                showAlert('Failed to create department', 'error');
            }
        } catch (error) {
            console.error('Error creating department:', error);
            showAlert('An error occurred while creating department', 'error');
        }
    };

    // Update department
    const handleUpdateDepartment = async () => {
        try {
            const { name, company } = formValues;
            if (!name || !company) {
                showAlert('Please fill all required fields', 'error');
                return;
            }

            const response = await fetch(`${API_BASE_URL}/modify-department/${selectedDepartment._id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name, company }),
            });

            const result = await response.json();
            if (result._id) {
                showAlert('Department updated successfully', 'success');
                setView('list');
                fetchDepartments();
                resetForm();
            } else {
                showAlert('Failed to update department', 'error');
            }
        } catch (error) {
            console.error('Error updating department:', error);
            showAlert('An error occurred while updating department', 'error');
        }
    };

    // Delete department
    const confirmDeleteDepartment = (department) => {
        setDepartmentToDelete(department);
        setDeleteDialogOpen(true);
    };

    const handleDeleteDepartment = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/delete-department/${departmentToDelete._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.ok) {
                setDepartments(departments.filter(dep => dep._id !== departmentToDelete._id));
                showAlert('Department deleted successfully', 'success');
            } else {
                showAlert('Failed to delete department', 'error');
            }
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting department:', error);
            showAlert('An error occurred while deleting department', 'error');
            setDeleteDialogOpen(false);
        }
    };

    // Edit department
    const handleEditDepartment = (department) => {
        setSelectedDepartment(department);
        setFormValues({
            name: department.name,
            company: department.company || '',
        });
        setView('edit');
    };

    // Helper functions
    const resetForm = () => {
        setFormValues({ name: '', company: '' });
        setSelectedDepartment(null);
    };

    const showAlert = (message, severity) => {
        setAlertInfo({ open: true, message, severity });
    };

    const handleCloseAlert = () => {
        setAlertInfo({ ...alertInfo, open: false });
    };

    const getCompanyName = (companyId) => {
        const company = companies.find(company => company._id === companyId);
        return company ? company.name : 'N/A';
    };

    // Render department table
    const renderDepartmentTable = () => (
        <TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Table aria-label="department table">
                <TableHead>
                    <TableRow>
                        <TableCell>Department Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {departments.length > 0 ? (
                        departments.map((department) => (
                            <TableRow key={department._id} hover>
                                <TableCell>{department.name}</TableCell>
                                <TableCell>{getCompanyName(department.company)}</TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditDepartment(department)}
                                        size="small"
                                        sx={{ mr: 1 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => confirmDeleteDepartment(department)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} align="center">No departments found</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Render department form
    const renderDepartmentForm = () => (
        <Card sx={{ mt: 3, p: 2 }}>
            <CardContent>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                        label="Department Name"
                        name="name"
                        variant="outlined"
                        fullWidth
                        required
                        value={formValues.name}
                        onChange={handleFormChange}
                        autoFocus
                    />
                    <TextField
                        select
                        label="Company"
                        name="company"
                        variant="outlined"
                        fullWidth
                        required
                        value={formValues.company}
                        onChange={handleFormChange}
                    >
                        {companies.map((company) => (
                            <MenuItem key={company._id} value={company._id}>
                                {company.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => {
                                setView('list');
                                resetForm();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={view === 'create' ? <Save /> : <AddCircle />}
                            onClick={view === 'create' ? handleCreateDepartment : handleUpdateDepartment}
                            color="primary"
                        >
                            {view === 'create' ? 'Create Department' : 'Update Department'}
                        </Button>
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );

    // Render main view
    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="lg" sx={{ py: 2 }}>
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h4" color="primary">
                            {view === 'list' && 'Departments'}
                            {view === 'create' && 'Create Department'}
                            {view === 'edit' && 'Edit Department'}
                        </Typography>

                        {view === 'list' ? <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setView('create')}
                        >
                            Add Department
                        </Button> : <Button
                            variant="contained"
                            color="warning"
                            startIcon={<Close />}
                            onClick={() => {
                                setView('list');
                                resetForm();
                            }}>
                            Cancel
                        </Button>}
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                </Box>

                {view === 'list' && renderDepartmentTable()}
                {(view === 'create' || view === 'edit') && renderDepartmentForm()}

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete the department "{departmentToDelete?.name}"?
                        This action cannot be undone.
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDeleteDepartment} color="error">Delete</Button>
                    </DialogActions>
                </Dialog>

                {/* Alert Snackbar */}
                <Snackbar
                    open={alertInfo.open}
                    autoHideDuration={5000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseAlert} severity={alertInfo.severity} variant="filled">
                        {alertInfo.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default DepartmentManagement;